import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { parseFilenameFromContentDisposition } from '../utils/downloadFile';

export const BASE_URL = process.env.REACT_APP_BASE_URL;

const handleBinaryDownloadResponse = (defaultFilename) => async (response) => {
  if (!response.ok) {
    try {
      return await response.json();
    } catch {
      return await response.text();
    }
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get('content-disposition');
  const filename = parseFilenameFromContentDisposition(
    contentDisposition,
    defaultFilename
  );

  return { blob, filename };
};

export const ADapi = createApi({
  reducerPath: 'menuApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  }),
  tagTypes: [
    'AuthKey',
    'LocalDomain',
    'DomainController',
    'UserWithProfile',
    'Collection',
    'Profile',
    'Domain',
    'Group',
    'Agent',
  ],
  endpoints: (build) => ({
    getMenu: build.query({
      query: () => '/content/rest_api/v1/cabinet_menu/',
      transformErrorResponse: (response) => response.status,
    }),
    getUserData: build.query({
      query: () => ({
        url: '/auth/is_authenticate',
      }),
    }),
    getAuthentication: build.query({
      query: () => ({
        url: '/auth/is_authenticate',
        method: 'POST',
      }),
    }),
    getAuthToken: build.query({
      query: () => ({
        url: '/api/token/by-session/',
        method: 'POST',
      }),
      transformResponse: (response) => {
        const { plan_features } = JSON.parse(
          atob(response.access.split('.')[1])
        );

        const {
          nets_agents,
          agents_active_directory,
          max_agent_domain_controllers,
          max_agent_local_records,
          max_desktop_agents,
        } = plan_features;

        const areLocalDomainsAvailable =
          max_agent_domain_controllers > 0 && max_agent_local_records > 0;
        const areNetsAgentsAvailable = nets_agents > 0;
        const areAgentsActiveDirectoryAvailable = agents_active_directory > 0;
        const isMaxDesktopAgentEnabled = max_desktop_agents > 0;

        return {
          access: response.access,
          areLocalDomainsAvailable,
          areNetsAgentsAvailable,
          areAgentsActiveDirectoryAvailable,
          isMaxDesktopAgentEnabled,
        };
      },
    }),
    getAuthKey: build.query({
      query: (user_id) =>
        `/agents/rest_api/v1/users/${user_id}/agent_auth_key/`,
      providesTags: ['AuthKey'],
      transformErrorResponse: (response) => response.status,
    }),
    createAuthKey: build.mutation({
      query: ({ user_id }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/agent_auth_key/`,
        method: 'POST',
      }),
      invalidatesTags: ['AuthKey'],
      transformErrorResponse: (response) => response.data,
    }),
    deleteAuthKey: build.mutation({
      query: ({ user_id }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/agent_auth_key/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AuthKey'],
      transformErrorResponse: (response) => response.data,
    }),
    getDomainControllers: build.query({
      query: (user_id) =>
        `/agents/rest_api/v1/users/${user_id}/domain_controller/`,
      transformResponse: (response) => response.results,
      providesTags: (result = []) => [
        'DomainController',
        ...result.map(({ id }) => ({ type: 'DomainController', id })),
      ],
    }),
    createDomainController: build.mutation({
      query: ({ user_id, domainController }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/domain_controller/`,
        method: 'POST',
        body: domainController,
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: ['DomainController', 'LocalDomain'],
    }),
    editDomainController: build.mutation({
      query: ({ user_id, domainController }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/domain_controller/${domainController.id}/`,
        method: 'PATCH',
        body: domainController,
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: ['DomainController'],
    }),
    deleteDomainController: build.mutation({
      query: ({ user_id, domainControllerId }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/domain_controller/${domainControllerId}/`,
        method: 'DELETE',
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: ['DomainController', 'LocalDomain'],
    }),
    getLocalDomains: build.query({
      query: (user_id) => `/agents/rest_api/v1/users/${user_id}/local_domain/`,
      transformResponse: (response) => response.results,
      providesTags: (result = []) => [
        'LocalDomain',
        ...result.map(({ id }) => ({ type: 'LocalDomain', id })),
      ],
    }),
    createLocalDomain: build.mutation({
      query: ({ user_id, localDomain }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/local_domain/`,
        method: 'POST',
        body: localDomain,
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: ['LocalDomain'],
    }),
    editLocalDomain: build.mutation({
      query: ({ user_id, localDomain }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/local_domain/${localDomain.id}/`,
        method: 'PATCH',
        body: localDomain,
      }),
      transformErrorResponse: (response) => response.data,
      invalidatesTags: ['LocalDomain'],
    }),
    deleteLocalDomain: build.mutation({
      query: ({ user_id, id }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/local_domain/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LocalDomain'],
    }),
    getAllAgents: build.query({
      // The argument will be the initial relative path: 'users/user_id/agentinfo/?page=1'
      queryFn: async (initialPath, api, extraOptions, baseQuery) => {
        let accumulatedResults = [];
        let nextLink = initialPath;

        try {
          while (nextLink) {
            // Use baseQuery to perform the individual network request
            const result = await baseQuery(nextLink, api, extraOptions);

            if (result.error) {
              // Stop and return the error if any page request fails
              return { error: result.error };
            }

            const response = result.data;
            accumulatedResults = accumulatedResults.concat(response.results);
            nextLink = response.next_page_link;
          }

          // Return the combined array as the success result for this query
          return { data: accumulatedResults };
        } catch (error) {
          return { error: { status: 'CUSTOM_ERROR', data: error } };
        }
      },
      providesTags: (result = []) => [
        'Agent',
        ...result.map(({ uid }) => {
          return { type: 'Agent', id: uid };
        }),
      ],
    }),
    getUsersWithProfiles: build.query({
      queryFn: async (user_id, api, extraOptions, baseQuery) => {
        let accumulatedResults = [];
        let nextLink = `/ad/rest_api/v1/users/${user_id}/ad_users_with_profiles/`;

        try {
          while (nextLink) {
            // Use baseQuery to perform the individual network request
            const result = await baseQuery(nextLink, api, extraOptions);

            if (result.error) {
              // Stop and return the error if any page request fails
              return { error: result.error };
            }

            const response = result.data;
            accumulatedResults = accumulatedResults.concat(response.results);
            nextLink = response.next_page_link;
          }

          // Return the combined array as the success result for this query
          return { data: accumulatedResults };
        } catch (error) {
          return { error: { status: 'CUSTOM_ERROR', data: error } };
        }
      },
      providesTags: ['UserWithProfile'],
    }),
    deleteAgent: build.mutation({
      query: ({ user_id, uid, token }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/agentinfo/${uid}/`,
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      invalidatesTags: ['Agent'],
    }),
    updateAgent: build.mutation({
      query: ({ user_id, uid, agentData, token }) => ({
        url: `/agents/rest_api/v1/users/${user_id}/agentinfo/${uid}/`,
        method: 'PATCH',
        body: agentData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      invalidatesTags: ['Agent'],
    }),
    getCollections: build.query({
      query: (user_id) => `/ad/rest_api/v1/users/${user_id}/collections/`,
      transformResponse: (response) => response.results,
      providesTags: ['Collection'],
    }),
    createCollection: build.mutation({
      query: ({ user_id, payload }) => ({
        url: `/ad/rest_api/v1/users/${user_id}/collections/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Collection', 'User'],
    }),
    updateCollection: build.mutation({
      query: ({ user_id, payload }) => ({
        url: `/ad/rest_api/v1/users/${user_id}/collections/${payload.id}/`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['Collection', 'User'],
    }),
    updateCollectionPriorities: build.mutation({
      query: ({ user_id, payload }) => ({
        url: `/ad/rest_api/v1/users/${user_id}/collections/priorities/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Collection'],
    }),
    deleteCollection: build.mutation({
      query: ({ user_id, collectionId }) => ({
        url: `/ad/rest_api/v1/users/${user_id}/collections/${collectionId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Collection', 'User'],
    }),
    getProfiles: build.query({
      query: (user_id) => `/dashboard/rest_api/v1/users/${user_id}/profiles/`,
      transformResponse: (response) => response.results,
      providesTags: ['Profile'],
    }),
    getDomains: build.query({
      query: (user_id) => `/ad/rest_api/v1/users/${user_id}/agent_domains/`,
      transformResponse: (response) => response.results,
      providesTags: ['Domain'],
    }),
    createDomain: build.mutation({
      query: ({ user_id, payload }) => ({
        url: `/ad/rest_api/v1/users/${user_id}/agent_domains/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Domain'],
    }),
    updateDomain: build.mutation({
      query: ({ user_id, id, payload }) => ({
        url: `/ad/rest_api/v1/users/${user_id}/agent_domains/${id}/`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['Domain'],
    }),
    deleteDomain: build.mutation({
      query: ({ user_id, id }) => ({
        url: `/ad/rest_api/v1/users/${user_id}/agent_domains/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Domain'],
    }),
    getGroups: build.query({
      query: (user_id) => `/ad/rest_api/v1/users/${user_id}/ad_groups/`,
      transformResponse: (response) => response.results,
      providesTags: ['Group'],
    }),
    getChangelog: build.query({
      query: () => '/api/public/rest/v2/agents/changelogs/',
      transformResponse: (response) => {
        const os = {};

        const allowedOs = ['windows', 'linux', 'macos'];

        response.results.forEach((result) => {
          if (allowedOs.includes(result.os)) {
            (os[result.os] ??= []).push(result);
          }
        });

        return os;
      },
    }),
    downloadAgentDebPackage: build.mutation({
      query: () => ({
        url: '/api/public/rest/v2/agents/builds/deb/download/',
        method: 'GET',
        responseHandler: handleBinaryDownloadResponse('Agent-Setup.deb'),
      }),
    }),
    downloadAgentRpmPackage: build.mutation({
      query: () => ({
        url: '/api/public/rest/v2/agents/builds/rpm/download/',
        method: 'GET',
        responseHandler: handleBinaryDownloadResponse('Agent-Setup.rpm'),
      }),
    }),
    downloadAgentExePackage: build.mutation({
      query: () => ({
        url: '/api/public/rest/v2/agents/builds/exe/download/',
        method: 'GET',
        responseHandler: handleBinaryDownloadResponse('Agent-Setup.exe'),
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetMenuQuery,
  useGetUserDataQuery,
  useGetAuthenticationQuery,
  useGetAuthTokenQuery,
  useGetAuthKeyQuery,
  useCreateAuthKeyMutation,
  useDeleteAuthKeyMutation,
  useGetDomainControllersQuery,
  useCreateDomainControllerMutation,
  useEditDomainControllerMutation,
  useDeleteDomainControllerMutation,
  useGetLocalDomainsQuery,
  useCreateLocalDomainMutation,
  useEditLocalDomainMutation,
  useDeleteLocalDomainMutation,
  useGetAllAgentsQuery,
  useDeleteAgentMutation,
  useUpdateAgentMutation,
  useGetUsersWithProfilesQuery,
  useGetCollectionsQuery,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useUpdateCollectionPrioritiesMutation,
  useDeleteCollectionMutation,
  useGetProfilesQuery,
  useGetDomainsQuery,
  useCreateDomainMutation,
  useUpdateDomainMutation,
  useDeleteDomainMutation,
  useGetGroupsQuery,
  useGetChangelogQuery,
  useDownloadAgentDebPackageMutation,
  useDownloadAgentRpmPackageMutation,
  useDownloadAgentExePackageMutation,
} = ADapi;
